import { useEffect } from "react"
import { Users, Building2, Inbox, Activity, Loader2 } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAdminStore } from "@/stores/admin-store"
import { SummaryCard, SummaryCardsGrid } from "@/components/SummaryCard"
import { Badge } from "@/components/ui/badge"

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function formatRelativeDate(dateStr: string | null): string {
  if (!dateStr) return "Never"
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  return formatDate(dateStr)
}

const statusVariant: Record<string, "default" | "warning" | "success" | "destructive"> = {
  pending: "warning",
  contacted: "info" as "default",
  converted: "success",
  rejected: "destructive",
}

export function AdminDashboardPage() {
  const navigate = useNavigate()
  const {
    dashboardStats,
    recentDemoRequests,
    recentOrganizations,
    isDashboardLoading,
    loadDashboard,
  } = useAdminStore()

  useEffect(() => {
    void loadDashboard()
  }, [loadDashboard])

  if (isDashboardLoading && !dashboardStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-6">
        {/* Summary Cards */}
        <SummaryCardsGrid>
          <SummaryCard
            title="Pending Requests"
            primary={String(dashboardStats?.pendingDemoRequests ?? 0)}
            secondary={dashboardStats?.pendingDemoRequests ? "Needs attention" : undefined}
            variant={dashboardStats?.pendingDemoRequests ? "warning" : "default"}
            href="/app/admin/demo-requests"
            icon={<Inbox className="h-4 w-4" />}
          />
          <SummaryCard
            title="Organisations"
            primary={String(dashboardStats?.totalOrganizations ?? 0)}
            href="/app/admin/organizations"
            icon={<Building2 className="h-4 w-4" />}
          />
          <SummaryCard
            title="Active Orgs (30d)"
            primary={String(dashboardStats?.activeOrganizations ?? 0)}
            secondary={
              dashboardStats?.totalOrganizations
                ? `of ${dashboardStats.totalOrganizations} total`
                : undefined
            }
            variant="success"
            href="/app/admin/organizations"
            icon={<Activity className="h-4 w-4" />}
          />
          <SummaryCard
            title="Total Users"
            primary={String(dashboardStats?.totalUsers ?? 0)}
            href="/app/admin/users"
            icon={<Users className="h-4 w-4" />}
          />
        </SummaryCardsGrid>

        {/* Recent Demo Requests */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium">Recent Demo Requests</h2>
            <button
              onClick={() => void navigate("/app/admin/demo-requests")}
              className="text-xs text-[hsl(var(--link))] hover:underline"
            >
              View all
            </button>
          </div>

          {recentDemoRequests.length === 0 ? (
            <p className="text-sm text-muted-foreground">No demo requests yet.</p>
          ) : (
            <div className="rounded-lg overflow-hidden bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Name</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Email</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Company</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {recentDemoRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-muted/30">
                      <td className="px-3 py-2">{`${request.firstName} ${request.lastName}`}</td>
                      <td className="px-3 py-2 text-muted-foreground">{request.email}</td>
                      <td className="px-3 py-2 text-muted-foreground">{request.company ?? "—"}</td>
                      <td className="px-3 py-2">
                        <Badge variant={statusVariant[request.status] ?? "default"}>
                          {request.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{formatDate(request.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Recent Organizations */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium">Recent Organisations</h2>
            <button
              onClick={() => void navigate("/app/admin/organizations")}
              className="text-xs text-[hsl(var(--link))] hover:underline"
            >
              View all
            </button>
          </div>

          {recentOrganizations.length === 0 ? (
            <p className="text-sm text-muted-foreground">No organisations yet.</p>
          ) : (
            <div className="rounded-lg overflow-hidden bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Organisation</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Users</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Bids</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Docs</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Last Active</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {recentOrganizations.map((org) => (
                    <tr key={org.id} className="hover:bg-muted/30">
                      <td className="px-3 py-2 font-medium">{org.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{org.userCount}</td>
                      <td className="px-3 py-2 text-muted-foreground">{org.bidCount}</td>
                      <td className="px-3 py-2 text-muted-foreground">{org.documentCount}</td>
                      <td className="px-3 py-2 text-muted-foreground">{formatRelativeDate(org.lastActiveAt)}</td>
                      <td className="px-3 py-2">
                        <Badge variant={org.isActive ? "success" : "destructive"}>
                          {org.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Usage & Cost */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium">Usage &amp; Cost</h2>
            <button
              onClick={() => void navigate("/app/admin/usage")}
              className="text-xs text-[hsl(var(--link))] hover:underline"
            >
              View all
            </button>
          </div>
          <p className="text-sm text-muted-foreground">
            Per-organisation LLM, Textract, embedding and rerank spend.
          </p>
        </section>
      </div>
    </div>
  )
}
